"use client";

import {
  cinematicModalPrimaryButtonClass,
  cinematicModalSecondaryButtonClass,
} from "@/lib/cinematicModalStyles";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { BookOpen, MessageCircle, X } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";

export interface WordActionAnchor {
  x: number;
  y: number;
  top?: number;
  bottom?: number;
}

interface WordActionBarProps {
  word: string;
  position: WordActionAnchor;
  onAskChicky?: (word: string) => void;
  onOpenFlashcard: (word: string, position: WordActionAnchor) => void;
  onClose: () => void;
}

export function WordActionBar({
  word,
  position,
  onAskChicky,
  onOpenFlashcard,
  onClose,
}: WordActionBarProps) {
  const reducedMotion = useReducedMotion();
  const barRef = React.useRef<HTMLDivElement>(null);
  const previouslyFocusedElementRef = React.useRef<HTMLElement | null>(null);
  const [portalRoot, setPortalRoot] = React.useState<HTMLElement | null>(null);
  const [adjustedPosition, setAdjustedPosition] = React.useState<WordActionAnchor | null>(null);

  React.useEffect(() => {
    previouslyFocusedElementRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setPortalRoot(document.body);
  }, []);

  React.useEffect(() => {
    if (portalRoot) barRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, [portalRoot]);

  React.useLayoutEffect(() => {
    if (!barRef.current) return;

    const bar = barRef.current;
    const rect = bar.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportPadding = 16;
    const verticalGap = 20;
    const anchorTop = position.top ?? position.y;

    let x = position.x - rect.width / 2;
    const y = Math.max(viewportPadding, anchorTop - rect.height - verticalGap);

    if (x + rect.width > viewportWidth - viewportPadding) {
      x = viewportWidth - rect.width - viewportPadding;
    }
    if (x < viewportPadding) {
      x = viewportPadding;
    }

    setAdjustedPosition({ x, y });
  }, [portalRoot, position]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        previouslyFocusedElementRef.current?.focus();
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleAskChicky = () => {
    onAskChicky?.(word);
    onClose();
  };

  const handleOpenFlashcard = () => {
    // The anchored action unmounts as details open. Give the modal a stable
    // focus-return target instead of the disappearing Flashcard button.
    previouslyFocusedElementRef.current?.focus();
    onOpenFlashcard(word, position);
    onClose();
  };

  const selectedWords = word.trim().split(/\s+/).filter(Boolean);
  const displayWord = selectedWords.length > 6 ? `${selectedWords.slice(0, 6).join(" ")}...` : word;

  const actionBar = (
    <AnimatePresence>
      <motion.div
        ref={barRef}
        initial={{ opacity: 0, scale: reducedMotion ? 1 : 0.95 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: reducedMotion ? 1 : 0.95 }}
        transition={{ duration: reducedMotion ? 0 : 0.15, ease: "easeOut" }}
        role="group"
        aria-label="Selected text actions"
        className="fixed z-[100] flex max-w-[min(360px,calc(100vw-32px))] flex-col gap-2 overflow-hidden rounded-lg border border-white/10 bg-[#121318] p-2 text-white shadow-[0_18px_48px_rgba(0,0,0,0.42)]"
        style={{
          left: `${adjustedPosition?.x ?? 0}px`,
          top: `${adjustedPosition?.y ?? 0}px`,
          visibility: adjustedPosition ? "visible" : "hidden",
          transformOrigin: "50% 100%",
        }}
      >
        <div className="flex items-center gap-1.5">
          {onAskChicky && (
            <button
              onClick={handleAskChicky}
              type="button"
              aria-label="Ask Chicky about selected text"
              className={`${cinematicModalPrimaryButtonClass} min-w-0 flex-1 px-3`}
            >
              <MessageCircle size={16} />
              <span>Ask</span>
            </button>
          )}

          <button
            onClick={handleOpenFlashcard}
            type="button"
            className={`${cinematicModalSecondaryButtonClass} min-w-0 flex-1 px-3`}
          >
            <BookOpen size={16} />
            <span>Flashcard</span>
          </button>

          <button
            onClick={() => {
              previouslyFocusedElementRef.current?.focus();
              onClose();
            }}
            type="button"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-white/10 text-white/55 transition-colors hover:bg-white/[0.06] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/60"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div
          className="max-w-full truncate border-t border-white/10 px-3 pt-2 pb-1 text-center text-sm text-white/70"
          title={word}
        >
          &ldquo;{displayWord}&rdquo;
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return portalRoot ? createPortal(actionBar, portalRoot) : null;
}
